import { useEffect, useLayoutEffect, useRef, useState, type PointerEvent, type ReactNode } from "react";
import { ArrowLeft, ChevronDown, ChevronLeft, Minus, X } from "lucide-react";

interface AdminPanelProps {
  open: boolean;
  title: string;
  subtitle?: string;
  onClose: () => void;
  children: ReactNode;
  /** Pinned under the scrolling body, e.g. the composer. */
  footer?: ReactNode;
  /** Extra header buttons, before the hide button. */
  tools?: ReactNode;
  /** Shows a back button that returns to the main view. */
  onBack?: () => void;
  /** Names the view on show; a new view starts at the top. */
  view?: string;
  /** Beside the title, e.g. Stax acting out what the builder is doing. */
  icon?: ReactNode;
  /** Navigation below Stax's header, kept outside the scrolling panel body. */
  tabs?: ReactNode;
  /** Simple mode uses the chat as a phone-sized page rather than a partial sheet. */
  fullScreen?: boolean;
  /**
   * A task that stands in for the chat (publishing) gets two clear ways out instead of the hide button:
   * back to Stax's chat, or close the panel entirely.
   */
  exits?: { onBackToStax: () => void; onClose: () => void };
}

/** Docked side panel on wide screens, bottom sheet on narrow ones. Non-modal: the website stays usable. */
export function AdminPanel({ open, title, subtitle, onClose, children, footer, tools, onBack, view, icon, tabs, fullScreen = false, exits }: AdminPanelProps) {
  const heading = useRef<HTMLHeadingElement>(null);
  const body = useRef<HTMLDivElement>(null);
  const previousFocus = useRef<HTMLElement | null>(null);
  const dragStart = useRef<number | null>(null);
  const [sheet, setSheet] = useState<"half" | "full">("half");
  // Kept on screen briefly after closing so it can slide away.
  const [leaving, setLeaving] = useState(false);
  const wasOpen = useRef(open);

  useEffect(() => {
    const closed = wasOpen.current && !open;
    wasOpen.current = open;
    if (!closed) {
      setLeaving(false);
      return;
    }
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    setLeaving(true);
    const timer = setTimeout(() => setLeaving(false), 220);
    return () => clearTimeout(timer);
  }, [open]);

  // Before the children's effects, so a view that starts elsewhere (the chat, at its latest) can.
  useLayoutEffect(() => {
    if (body.current) body.current.scrollTop = 0;
  }, [view]);

  useEffect(() => {
    if (open) {
      previousFocus.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      heading.current?.focus({ preventScroll: true });
    } else {
      previousFocus.current?.focus({ preventScroll: true });
    }
  }, [open, title]);

  const finishDrag = (event: PointerEvent<HTMLButtonElement>) => {
    if (dragStart.current === null) return;
    const moved = dragStart.current - event.clientY;
    dragStart.current = null;
    if (Math.abs(moved) > 20) setSheet(moved > 0 ? "full" : "half");
  };

  return (
    <section
      id="bw-admin-panel"
      className="bw-panel"
      aria-labelledby="bw-admin-title"
      hidden={!open && !leaving}
      inert={!open || undefined}
      data-leaving={(!open && leaving) || undefined}
      data-sheet={sheet}
      data-fullscreen={fullScreen || undefined}
      data-task={exits ? true : undefined}
      onKeyDown={(event) => {
        if (event.key === "Escape") {
          event.stopPropagation();
          (exits?.onClose ?? onClose)();
          return;
        }
        if (event.key !== "Tab" || fullScreen) return;
        const controls = Array.from(event.currentTarget.querySelectorAll<HTMLElement>('button:not(:disabled), [href], input, textarea, select, [tabindex]:not([tabindex="-1"])'));
        const first = controls[0];
        const last = controls.at(-1);
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first?.focus();
        }
      }}
    >
      {!fullScreen && !exits && <button
        type="button"
        className="bw-sheet-handle"
        aria-label={sheet === "half" ? "Expand panel" : "Make panel half height"}
        onClick={() => setSheet((current) => current === "half" ? "full" : "half")}
        onPointerDown={(event) => { dragStart.current = event.clientY; event.currentTarget.setPointerCapture(event.pointerId); }}
        onPointerUp={finishDrag}
      />}
      {exits ? (
        <div className="bw-panel-head bw-panel-head--task">
          <div className="bw-panel-exits">
            <button type="button" className="bw-btn bw-btn--text bw-back-to-stax" onClick={exits.onBackToStax}>
              <ChevronLeft size={20} aria-hidden="true" />Back to Stax
            </button>
            <button type="button" className="bw-btn bw-btn--icon bw-panel-close" onClick={exits.onClose} aria-label="Close">
              <X size={18} aria-hidden="true" />
            </button>
          </div>
          <div className="bw-panel-title">
            {icon}
            <div>
              <h2 id="bw-admin-title" ref={heading} tabIndex={-1}>{title}</h2>
              {subtitle && <p>{subtitle}</p>}
            </div>
          </div>
        </div>
      ) : <div className="bw-panel-head">
        {onBack && (
          <button type="button" className="bw-btn bw-btn--icon" onClick={onBack} aria-label="Back to the conversation">
            <ArrowLeft size={20} aria-hidden="true" />
          </button>
        )}
        {icon}
        <div>
          <h2 id="bw-admin-title" ref={heading} tabIndex={-1}>{title}</h2>
          {subtitle && <p>{subtitle}</p>}
        </div>
        {tools}
        <button type="button" className="bw-btn bw-btn--icon" onClick={onClose} aria-label="Hide panel and browse your website">
          {fullScreen ? <ChevronDown size={20} aria-hidden="true" /> : <Minus size={20} aria-hidden="true" />}
        </button>
      </div>}
      {tabs}
      <div className="bw-panel-body" ref={body}>{children}</div>
      {footer}
    </section>
  );
}
